import {
  Chip,
  Group,
  Icon,
  Stack,
  Text,
  Title,
  TooltipIcon,
} from "@assetwatch/ui";
import { groupAssetsByMachine, type QueryCost, queryCostParts } from "./lib";
import type { RailItem } from "./types";

function AssetIdentity({
  customer,
  machine,
  facility,
}: {
  customer: string;
  machine: string;
  facility: string;
}) {
  return (
    <Group align="center" gap={8} wrap="nowrap">
      <Icon name="asset" size="xl" tone="secondary" />
      <Stack gap={2}>
        <Title size="md" weight="bold">
          {customer || "Unknown customer"}
        </Title>
        {machine ? <Text size="sm">{machine}</Text> : null}
        {facility ? (
          <Group align="center" gap={4} wrap="nowrap">
            <Icon name="location-pin-solid" size="sm" tone="secondary" />
            <Text size="sm" tone="subtle">
              {facility}
            </Text>
          </Group>
        ) : null}
      </Stack>
    </Group>
  );
}

function AssetScore() {
  return (
    <Stack gap={2}>
      <Text size="sm" tone="subtle">
        Status
      </Text>
      <Chip icon="dot" label="Pending" tone="negative" />
    </Stack>
  );
}

function MachineCluster({
  group,
  selectedId,
  onOpen,
}: {
  group: { key: string; machine: string; items: RailItem[] };
  selectedId: string | undefined;
  onOpen: (id: string) => void;
}) {
  const [first] = group.items;
  if (!first) {
    return null;
  }

  return (
    <div className="flex overflow-hidden rounded-(--radius-sm) border border-outline-subtle bg-informative-container">
      <span className="w-(--spacing-4) shrink-0 bg-informative" />
      <div className="flex min-w-0 flex-1 flex-col gap-(--spacing-12) px-(--spacing-12) py-(--spacing-12)">
        <AssetIdentity
          customer={first.customer_name}
          facility={first.facility}
          machine={group.machine}
        />
        <Stack gap={8}>
          {group.items.map((item) => {
            const selected = item.id === selectedId;
            return (
              <button
                aria-current={selected ? "true" : undefined}
                // Hover outlines the item so it reads as clickable; the open
                // item keeps its stronger ring rather than flickering on hover.
                className={`cursor-pointer rounded-(--radius-sm) bg-surface px-(--spacing-12) py-(--spacing-8) text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-button-focus-ring ${selected ? "ring-2 ring-primary" : "hover:bg-surface-hover hover:ring-1 hover:ring-outline"}`}
                key={item.id}
                onClick={() => onOpen(item.id)}
                type="button"
              >
                <Stack gap={8}>
                  <Group gap={8} justify="between" wrap="nowrap">
                    <Text weight="bold">{item.monitoring_point}</Text>
                    {item.reviewed ? (
                      <Icon
                        aria-label="All features reviewed"
                        name="circle-check"
                        size="sm"
                        tone="positive"
                      />
                    ) : null}
                  </Group>
                  {item.reviewed ? null : <AssetScore />}
                </Stack>
              </button>
            );
          })}
        </Stack>
      </div>
    </div>
  );
}

/**
 * The feedback items down the left of the page. This is the page's
 * own rail, not the app sidebar — the sidebar lists pages, and a page never
 * adds to it.
 */
export function ItemRail({
  items,
  selectedId,
  onOpen,
  cost,
}: {
  items: RailItem[];
  selectedId: string | undefined;
  onOpen: (id: string) => void;
  /** The day's Athena scan, behind an info icon so the narrow rail stays quiet. */
  cost: QueryCost | null;
}) {
  const groups = groupAssetsByMachine(items);
  const pending = items.filter((item) => !item.reviewed).length;

  return (
    // Fills its box; the heading stays put and only the items scroll.
    <div className="flex min-h-0 flex-1 flex-col gap-(--spacing-12)">
      <Stack gap={2}>
        <Title size="sm" weight="bold">
          Feedback items
        </Title>
        <Group align="center" gap={4} wrap="nowrap">
          <Text size="sm" tone="subtle">
            {pending} of {items.length} awaiting review
          </Text>
          {cost ? (
            <TooltipIcon
              description={`${queryCostParts(cost).join(" · ")}. Estimated Athena scan cost to load this day.`}
              label="Scan estimate"
              size="xs"
            />
          ) : null}
        </Group>
      </Stack>
      <nav
        aria-label="Feedback items"
        className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]"
      >
        <Stack gap={12}>
          {items.length === 0 ? (
            <Text size="sm" tone="subtle">
              This CME has nothing to review on this date.
            </Text>
          ) : null}
          {groups.map((group) => (
            <MachineCluster
              group={group}
              key={group.key}
              onOpen={onOpen}
              selectedId={selectedId}
            />
          ))}
        </Stack>
      </nav>
    </div>
  );
}
